<template>
  <div
    :id="ids.main"
    v-click-outside="event => handleShow(event, false)"
    class="popover-wrapper inline cursor-pointer"
  >
    <div
      ref="main"
      class="popover-main flex"
      :class="popoverMainClass"
      @click="event => handleShow(event)"
      @mouseover="event => handleShow(event, true)"
      @mouseout="event => handleShow(event, false)"
    >
      <slot />
    </div>

    <div
      v-show="status"
      :id="ids.content"
      ref="contentRef"
      class="popover-popup absolute z-[99]"
      :class="innerClass"
      :style="{
        maxWidth: component.popoverWidth
      }"
    >
      <Component 
        :is="theme === 'info' ? V2AtomsTooltip : AtomsTooltip"
        :append-popover-class="appendPopoverClass"
        :tip-x="tipX"
        :tip-y="tipY"
        :type="theme"
        :radius="radius"
        :tip-x-adjustment="tipAdjustment.x"
        :tip-in-side="['left', 'right'].includes(tip)"
        :has-close="hasClose"
        :small-close="smallClose"
        :has-icon="false"
        always-show
        :model-value="true"
        @close="handleShow({ type: 'click' }, false)"
      >
        <template #title>
          <slot name="title" />
        </template>

        <template #default>
          <slot name="content" />
        </template>
      </Component>
    </div>
  </div>
</template>

<script setup>
import { AtomsTooltip, V2AtomsTooltip } from '#components'
import {
  generateUID,
  getKey
} from '~/helpers/functions.js'

defineOptions({
  name: 'AtomsPopoverV2'
})

const props = defineProps({
  theme: {
    type: String,
    default: 'default',
    validator: value => ['default', 'dark', 'purple', 'info'].includes(value)
  },

  position: {
    type: String,
    default: 'bottom',
    validator: value => ['top', 'bottom', 'left', 'right'].includes(value)
  },

  hasClose: {
    type: Boolean,
    default: false
  },

  smallClose: {
    type: Boolean,
    default: false
  },

  hasShadow: {
    type: Boolean,
    default: false
  },

  actionType: {
    type: String,
    default: 'hover',
    validator: value => ['click', 'hover'].includes(value)
  },

  popoverMargin: {
    type: Number,
    default: 6
  },

  popoverWidth: {
    type: [String, Number],
    default: '288px'
  },

  /**
   * @params {String}
   * @value 2md, md, lg
   */
  radius: {
    type: String,
    default: ''
  },

  popoverMainClass: {
    type: String,
    default: ''
  },

  appendPopoverClass: {
    type: String,
    default: ''
  },

  innerClass: {
    type: String,
    default: ''
  },

  positionLeftAdjustment: {
    type: Number,
    default: 0
  }
})

const content = ref({})
const contentRef = ref()
const status = ref(false)
const tip = ref('')
const tipAdjustment = ref({
  x: '0',
  y: '0'
})
const main = ref(null)

const component = computed(() => {
  const popoverWidth = `${props.popoverWidth}`.replace('px', '')

  return {
    popoverWidth: popoverWidth + (popoverWidth === 'auto' ? '' : 'px')
  }
})

const ids = computed(() => {
  return {
    main: generateUID('popover'),
    content: generateUID('popover-content')
  }
})

const tipY = computed(() => {
  if (['top', 'bottom'].includes(tip.value)) {
    return tip.value === 'bottom' ? 'top' : 'bottom'
  }

  if (['left', 'right'].includes(tip.value)) {
    return 'middle'
  }
  return 'middle'
})

const tipX = computed(() => {
  if (['left', 'right'].includes(tip.value)) {
    return tip.value === 'left' ? 'right' : 'left'
  }

  return 'center'
})

onMounted(async () => {
  await nextTick()

  if (contentRef.value) {
    window.document.body.appendChild(contentRef.value)

    // Create a temporary clone to measure dimensions
    const cloneContent = contentRef.value.cloneNode(true)
    cloneContent.style.display = 'block'
    cloneContent.style.position = 'absolute'
    cloneContent.style.top = '-9999px'
    cloneContent.style.left = '-9999px'
    cloneContent.style.visibility = 'hidden'

    window.document.body.appendChild(cloneContent)

    // Store dimensions
    content.value = {
      width: cloneContent.offsetWidth,
      height: cloneContent.offsetHeight
    }

    cloneContent.remove()

    window.addEventListener('resize', () => {
      if (status.value) {
        handleShow({ type: 'resize' }, true)
      }
    })
    window.addEventListener('scroll', () => {
      if (status.value) {
        handleShow({ type: 'scroll' }, true)
      }
    })
  }
})

onBeforeUnmount(() => {
  const contentVal = contentRef.value

  if (contentVal) {
    contentVal.remove()
  }

  window.removeEventListener('resize', calculatePositionOffscreen)
  window.removeEventListener('scroll', calculatePositionOffscreen)
})

const handleShow = async ($event, show = null) => {
  const eventType = $event?.type

  const actionMap = getKey(eventType, {
    click: 'click',
    mouseover: 'hover',
    mouseout: 'hover'
  })

  if (props.actionType !== actionMap) {
    return
  }

  if (show === null) {
    status.value = !status.value
  } else {
    status.value = show
  }

  const contentEl = contentRef.value

  if (contentEl) {
    if (status.value) {
      // Calculate position BEFORE showing the element
      const position = await calculatePositionOffscreen()

      // Apply position and show in one go
      contentEl.style.top = `${position.top}px`
      contentEl.style.left = `${position.left}px`
      contentEl.style.display = 'block'

      // Add show class after a single frame
      requestAnimationFrame(() => {
        contentEl.classList.add('popover-show')
      })
    } else {
      contentEl.classList.remove('popover-show')
      // Hide after animation completes
      setTimeout(() => {
        if (!status.value) {
          contentEl.style.display = 'none'
        }
      }, 200)
    }
  }
}

const calculatePositionOffscreen = () => {
  if (!main.value) return { top: 0, left: 0 }

  const windowWidth = window.innerWidth || document.documentElement.clientWidth
  const windowHeight = window.innerHeight || document.documentElement.clientHeight

  const mainRect = main.value.getBoundingClientRect()

  // Use the cloned content dimensions (created in onMounted)
  let contentRect
  if (content.value && content.value.width && content.value.height) {
    contentRect = {
      width: content.value.width,
      height: content.value.height
    }
  } else {
    // Fallback to estimated dimensions
    contentRect = {
      width: parseInt(props.popoverWidth) || 288,
      height: 100 // Estimate
    }
  }

  const scrollTop = window.pageYOffset || document.documentElement.scrollTop
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
  const margin = props.popoverMargin

  // Calculate absolute positions
  const mainAbsoluteTop = mainRect.top + scrollTop
  const mainAbsoluteLeft = mainRect.left + scrollLeft

  // Check viewport space
  const viewport = {
    top: mainRect.top >= (contentRect.height + margin),
    bottom: (mainRect.bottom + contentRect.height + margin) <= windowHeight,
    left: mainRect.left >= (contentRect.width + margin + props.positionLeftAdjustment),
    right: (mainRect.right + contentRect.width + margin) <= windowWidth
  }

  // Determine final position
  let finalPosition = props.position
  if (props.position === 'bottom' && !viewport.bottom && viewport.top) {
    finalPosition = 'top'
  } else if (props.position === 'top' && !viewport.top && viewport.bottom) {
    finalPosition = 'bottom'
  } else if (props.position === 'left' && !viewport.left && viewport.right) {
    finalPosition = 'right'
  } else if (props.position === 'right' && !viewport.right && viewport.left) {
    finalPosition = 'left'
  }

  tip.value = finalPosition

  let top, left

  switch (finalPosition) {
    case 'bottom':
      top = mainAbsoluteTop + mainRect.height + margin
      left = mainAbsoluteLeft + (mainRect.width / 2) - (contentRect.width / 2)
      left = Math.max(scrollLeft + 10, Math.min(left, scrollLeft + windowWidth - contentRect.width - 10))
      break

    case 'top':
      top = mainAbsoluteTop - contentRect.height - margin
      left = mainAbsoluteLeft + (mainRect.width / 2) - (contentRect.width / 2)
      left = Math.max(scrollLeft + 10, Math.min(left, scrollLeft + windowWidth - contentRect.width - 10))
      break

    case 'right':
      top = mainAbsoluteTop + (mainRect.height / 2) - (contentRect.height / 2)
      left = mainAbsoluteLeft + mainRect.width + margin
      top = Math.max(scrollTop + 10, Math.min(top, scrollTop + windowHeight - contentRect.height - 10))
      break

    case 'left':
      top = mainAbsoluteTop + (mainRect.height / 2) - (contentRect.height / 2)
      left = mainAbsoluteLeft - contentRect.width - margin - props.positionLeftAdjustment
      top = Math.max(scrollTop + 10, Math.min(top, scrollTop + windowHeight - contentRect.height - 10))
      break

    default:
      top = mainAbsoluteTop + mainRect.height + margin
      left = mainAbsoluteLeft + (mainRect.width / 2) - (contentRect.width / 2)
  }

  tipAdjustment.value.x = ''

  return {
    top: Math.round(top),
    left: Math.round(left)
  }
}
</script>
